import type {
    DomainStatItem,
    DomainStatsResponse,
    DomainType,
    VendorGroupBy,
} from '../../types/reporting';

/**
 * Fetch cap for overview bundle rankings and capped /domain/stats widgets when
 * showAllRows is off. Matches the full-grid display max (gridSpan >= 4 → 20).
 * All-rows / domain tabs omit limit instead.
 */
export const REPORTING_OVERVIEW_BUNDLE_LIMIT = 20;

/** Single bundle location filter for the overview dashboard (avoid per-widget query keys). */
export function getOverviewBundleLocationId(locationId?: string): string | undefined {
    return locationId;
}

/**
 * Preference grouping is never on the overview bundle; always use /domain/stats.
 */
export function needsDedicatedDomainStats(
    domain: DomainType,
    groupBy?: VendorGroupBy,
): boolean {
    return domain === 'vendor' && groupBy === 'preference';
}

/** @deprecated Use needsDedicatedDomainStats */
export const needsLiveDomainStats = needsDedicatedDomainStats;

function metricNumber(item: object, metric: string): number {
    const value = (item as Record<string, unknown>)[metric];
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function priorMetricNumber(item: { prior?: object }, metric: string): number {
    if (!item.prior) return 0;
    return metricNumber(item.prior, metric);
}

/**
 * Primary sort by current metric; when values tie and prior bars are shown,
 * break the tie with the same metric's prior-period value.
 */
export function compareMetricWithPriorTiebreaker(
    a: { prior?: object },
    b: { prior?: object },
    metric: string,
    sortOrder: 'asc' | 'desc',
    usePriorTiebreaker: boolean,
): number {
    const aVal = metricNumber(a, metric);
    const bVal = metricNumber(b, metric);
    if (aVal !== bVal) {
        return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    }
    if (!usePriorTiebreaker) return 0;
    const aPrior = priorMetricNumber(a, metric);
    const bPrior = priorMetricNumber(b, metric);
    return sortOrder === 'desc' ? bPrior - aPrior : aPrior - bPrior;
}

export function sortDomainStatItems(
    items: DomainStatItem[],
    sortBy: string,
    sortOrder: 'asc' | 'desc',
    options?: { priorTiebreaker?: boolean },
): DomainStatItem[] {
    const usePriorTiebreaker = Boolean(options?.priorTiebreaker);
    return [...items].sort((a, b) =>
        compareMetricWithPriorTiebreaker(a, b, sortBy, sortOrder, usePriorTiebreaker),
    );
}

export function applyDomainStatsPresentation(
    response: DomainStatsResponse,
    options: {
        sortBy: string;
        sortOrder: 'asc' | 'desc';
        limit?: number;
        priorTiebreaker?: boolean;
    },
): DomainStatsResponse {
    const sorted = sortDomainStatItems(response.items, options.sortBy, options.sortOrder, {
        priorTiebreaker: options.priorTiebreaker,
    });
    const items = options.limit && options.limit > 0
        ? sorted.slice(0, options.limit)
        : sorted;
    return { ...response, items };
}
